import { rjf01Scene } from '../../preview/scenes/rjf01'
import {
  AvatarDescriptor,
  RobloxMaterial,
  SceneDescriptor,
  SceneObjectDescriptor,
  Vec3,
} from '../../preview/types'
import { MissionResponseData } from '../../openapi'

/**
 * おためしプレイの scene 登録テーブル。
 *
 * キーは ProjectProvider が組み立てる `${slug}_${num}` 形式（小文字）に揃える。
 * 例: online サイトの RJF_01 は `rb1_1`、license サイトの RJF_01 は `rjf_1` など。
 *
 * 未登録ミッションは `buildSceneFromMissionObjects` で MissionData.objects 名から
 * 動的にシーンを組み立てるためのフォールバックがある（呼び出し側 editor 参照）。
 */
const SCENE_BY_MISSION_KEY: Readonly<Record<string, SceneDescriptor>> = {
  rb1_1: rjf01Scene,
  rjf_1: rjf01Scene,
  rj1_1: rjf01Scene,
}

const buildMissionKey = (
  slug: string | null | undefined,
  num: number | null | undefined,
): string | undefined => {
  if (!slug || typeof num !== 'number' || !Number.isFinite(num)) {
    return undefined
  }
  return `${slug.trim().toLowerCase()}_${num}`
}

export const getSceneForMission = (
  missionInfo: MissionResponseData | undefined,
): SceneDescriptor | undefined => {
  if (!missionInfo) return undefined
  const key = buildMissionKey(missionInfo.slug, missionInfo.num)
  if (!key) return undefined
  return SCENE_BY_MISSION_KEY[key]
}

// --- 動的シーン生成 -----------------------------------------------------

/** Roblox 風の既定カラーパレット（パーツ index で循環）。 */
const PARTS_PALETTE: readonly string[] = [
  '#ff69b4', // ピンク
  '#3fa34d', // 緑
  '#f5c518', // 黄
  '#c45a8e', // SpawnLocation 風の薄紫
  '#5fa8d3', // 青
  '#e67e22', // オレンジ
  '#c0392b', // 赤
  '#9b59b6', // 紫
]
const DEFAULT_MATERIAL: RobloxMaterial = 'Plastic'

const SAFE_ID_PREFIX = 'obj'

/** name に依存しない安定 ASCII id を生成（Roblox 名は全角を含むため）。 */
const safeId = (index: number): string => `${SAFE_ID_PREFIX}_${index + 1}`

/** N オブジェクトを 正方形寄りグリッドに並べる際の列数。 */
const computeGridCols = (n: number): number => {
  if (n <= 1) return 1
  return Math.ceil(Math.sqrt(n))
}

/** index から (x, z) のグリッド位置を返す（中心を 0 にそろえる）。 */
const computeGridPosition = (
  index: number,
  total: number,
  spacing: number,
): { x: number; z: number } => {
  const cols = computeGridCols(total)
  const row = Math.floor(index / cols)
  const col = index % cols
  const rows = Math.ceil(total / cols)
  return {
    x: (col - (cols - 1) / 2) * spacing,
    z: (row - (rows - 1) / 2) * spacing,
  }
}

const DEFAULT_AVATAR: AvatarDescriptor = {
  spawn: { x: -10, y: 1.2, z: 0 },
  size: { x: 1.2, y: 2.2, z: 1.2 },
  speed: 8,
  color: '#2e86de',
}

const DEFAULT_CAMERA: { position: Vec3; target: Vec3 } = {
  // 自由視点の初期位置はやや遠目で全景が見えるようにする。
  position: { x: 32, y: 26, z: 42 },
  target: { x: 0, y: 1, z: 0 },
}

const BASEPLATE: SceneObjectDescriptor = {
  id: 'baseplate',
  name: 'Baseplate',
  shape: 'box',
  position: { x: 0, y: -0.5, z: 0 },
  size: { x: 40, y: 1, z: 40 },
  color: '#a3a2a5',
  material: 'Plastic',
  isStatic: true,
}

/**
 * MissionData.objects[].name のリストから動的に SceneDescriptor を生成する。
 *
 * Roblox Studio のテンプレート(.rbxl)を解析しないため、位置/サイズ/色は
 * 自動配置（グリッド・パレット）になる。ただし name は MissionData 由来なので、
 * 利用側の Blockly workspace（同 name キー）とは確実に一致し、ハンドラ登録
 * （onPlay/onMessage/onTouchCharacter）が機能する。
 *
 * 名前が空・重複・Baseplate と衝突 する入力は安全に弾く（重複は最初の出現を採用）。
 */
export const buildSceneFromMissionObjects = (
  missionInfo: MissionResponseData | undefined,
  objectNames: readonly string[],
): SceneDescriptor => {
  const seen = new Set<string>()
  const sanitized: string[] = []
  for (const name of objectNames) {
    if (typeof name !== 'string') continue
    const trimmed = name.trim()
    if (!trimmed) continue
    if (trimmed === BASEPLATE.name) continue
    if (seen.has(trimmed)) continue
    seen.add(trimmed)
    sanitized.push(trimmed)
  }

  const spacing = 6
  const objects: SceneObjectDescriptor[] = [
    BASEPLATE,
    ...sanitized.map((name, index): SceneObjectDescriptor => {
      const grid = computeGridPosition(index, sanitized.length, spacing)
      return {
        id: safeId(index),
        name,
        shape: 'box',
        position: { x: grid.x, y: 1, z: grid.z },
        size: { x: 4, y: 1, z: 4 },
        color: PARTS_PALETTE[index % PARTS_PALETTE.length],
        material: DEFAULT_MATERIAL,
      }
    }),
  ]

  const missionId =
    buildMissionKey(missionInfo?.slug, missionInfo?.num) ?? 'unknown'
  const title = missionInfo?.title?.trim() ?? ''

  return {
    missionId,
    title,
    objects,
    avatar: DEFAULT_AVATAR,
    camera: DEFAULT_CAMERA,
  }
}
